import axios from 'axios';
import { API_URL } from '../config';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get token from localStorage or cookie
export const getAuthToken = () => {
  if (typeof window === 'undefined') return null;
  const localToken = localStorage.getItem('jwtToken');
  if (localToken) return localToken;

  const cookies = document.cookie.split('; ');
  const tokenCookie = cookies.find((row) => row.startsWith('accessToken='));
  return tokenCookie ? tokenCookie.split('=')[1] : null;
};

// Request interceptor: automatically attach Authorization header
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 Unauthorized with automatic token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const isAuthRoute =
      originalRequest.url &&
      (originalRequest.url.includes('/api/login') ||
        originalRequest.url.includes('/api/refresh'));

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const res = await axios.post(
          `${API_URL}/api/refresh`,
          { refreshToken },
          { withCredentials: true }
        );
        const newToken = res.data.accessToken;

        localStorage.setItem('jwtToken', newToken);
        const isSecure =
          typeof window !== 'undefined' && window.location.protocol === 'https:';
        document.cookie = `accessToken=${newToken}; path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`;

        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('refreshToken');
        document.cookie =
          'accessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC';
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    const friendly = translateApiError(error);
    error.friendlyMessage = friendly;
    if (error.response?.data) {
      if (typeof error.response.data === 'object') {
        const currentErr = String(error.response.data.error || '').toLowerCase();
        if (!error.response.data.error || currentErr === 'not found' || currentErr === 'internal server error') {
          error.response.data.error = friendly;
        }
      }
    }

    return Promise.reject(error);
  }
);

// Map standard HTTP and server errors into clear, friendly Romanian messages
export const translateApiError = (error) => {
  if (!error) return 'A apărut o eroare necunoscută.';

  const status = error.response?.status;
  const rawData = error.response?.data;
  const rawMsg = typeof rawData === 'string' ? rawData : (rawData?.error || rawData?.message || error.message || '');
  const lower = String(rawMsg).toLowerCase().trim();

  // 404 - Not Found
  if (lower === 'not found' || lower.includes('status code 404') || status === 404) {
    if (lower.includes('user not found') || lower.includes('utilizator')) {
      return 'Utilizatorul solicitat nu a fost găsit în sistem.';
    }
    if (lower.includes('producer not found') || lower.includes('producator')) {
      return 'Producătorul specificat nu a fost găsit.';
    }
    if (lower.includes('document')) {
      return 'Documentul solicitat nu a fost găsit pe server.';
    }
    if (lower.includes('sesiune') || lower.includes('session')) {
      return 'Sesiunea de confirmare nu a fost găsită sau a expirat.';
    }
    return 'Resursa sau serviciul solicitat nu a fost găsit pe server (Eroare 404).';
  }

  // 500 - Internal Server Error
  if (lower === 'internal server error' || lower.includes('status code 500') || status === 500) {
    return 'A apărut o eroare internă pe server. Vă rugăm să reîncercați.';
  }

  // 502 / 503 - Bad Gateway / Service Unavailable
  if (lower.includes('backend api unavailable') || lower.includes('bad gateway') || status === 502 || status === 503) {
    return 'Serviciul backend este momentan indisponibil sau se repornește. Vă rugăm să reîncercați în câteva secunde.';
  }

  // 401 - Unauthorized
  if (lower.includes('access denied') || lower.includes('unauthorized') || status === 401) {
    return 'Sesiunea dumneavoastră a expirat. Vă rugăm să vă autentificați din nou.';
  }

  // 403 - Forbidden
  if (lower.includes('invalid token') || lower.includes('token expired') || status === 403) {
    return 'Nu aveți permisiunea necesară pentru a efectua această acțiune.';
  }

  // Network / Timeout
  if (lower.includes('network error')) {
    return 'Eroare de conexiune la rețea. Verificați conexiunea la internet și reîncercați.';
  }
  if (lower.includes('timeout')) {
    return 'Timpul de așteptare a expirat. Vă rugăm să reîncercați.';
  }

  // Dacă există deja un mesaj util și clar în limba română (non-generic), îl folosim
  if (rawMsg && typeof rawMsg === 'string' && !rawMsg.startsWith('Request failed')) {
    return rawMsg;
  }

  return 'A apărut o eroare la procesarea solicitării. Vă rugăm să reîncercați.';
};

export default api;
